import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Camera, X, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

/**
 * Modal d'inspection nacelle réutilisable.
 * Pré-rempli avec contrat_id, client_nom, dossier_id et type_inspection.
 *
 * Props :
 *   open: boolean
 *   onClose: fn
 *   onSaved: fn(inspection)
 *   equipementId: string (REQUIRED)
 *   equipementNom: string (display only)
 *   typeInspection: 'pre_location' | 'post_location' | 'routine'
 *   contratId: string (optional)
 *   clientNom: string (optional)
 *   dossierId: string (optional)
 */
export function InspectionDialog({
  open, onClose, onSaved,
  equipementId, equipementNom = '',
  typeInspection = 'routine',
  contratId, clientNom, dossierId,
}) {
  const { user } = useAuth();
  const [template, setTemplate] = useState([]);
  const [items, setItems] = useState([]);
  const [notesGen, setNotesGen] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !equipementId) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/equipements-inventaire/${equipementId}/inspection-template`);
        const tpl = res.data?.template || [];
        setTemplate(tpl);
        setItems(tpl.map(t => ({ id: t.id, label: t.label, checked: true, notes: '' })));
        setNotesGen('');
        setPhotos([]);
      } catch (e) {
        console.error(e);
        toast.error("Impossible de charger le formulaire d'inspection");
      }
    })();
  }, [open, equipementId]);

  if (!open) return null;

  const toggleItem = (id) => setItems(prev => prev.map(i => i.id === id ? { ...i, checked: !i.checked } : i));
  const updateItemNotes = (id, notes) => setItems(prev => prev.map(i => i.id === id ? { ...i, notes } : i));

  const uploadPhoto = async (file) => {
    if (!file) return;
    try {
      setUploadingPhoto(true);
      const { compressImage } = await import('@/lib/imageCompression');
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append('file', compressed);
      const res = await axios.post(`${API_URL}/api/equipements-inventaire/upload-photo`, fd);
      if (res.data?.photo_url) {
        setPhotos(prev => [...prev, res.data.photo_url]);
      }
    } catch (e) {
      console.error('Erreur upload photo:', e);
      toast.error('Erreur upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const conforme = items.every(i => i.checked);
      const payload = {
        equipement_id: equipementId,
        inspecteur_nom: user ? `${user.prenom || ''} ${user.nom || ''}`.trim() : 'Inspecteur',
        items,
        notes_generales: notesGen,
        photos,
        conforme,
        type_inspection: typeInspection,
        contrat_id: contratId || null,
        client_nom: clientNom || null,
        dossier_id: dossierId || null,
      };
      const res = await axios.post(`${API_URL}/api/equipements-inventaire/${equipementId}/inspections`, payload);
      toast.success(conforme ? '✓ Inspection conforme enregistrée' : '⚠️ Inspection non conforme enregistrée');
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'enregistrement de l'inspection");
    }
    setSaving(false);
  };

  const titrePartType = {
    pre_location: '📥 Inspection AVANT location',
    post_location: '📤 Inspection APRÈS location (au retour)',
    routine: '🔧 Inspection de routine',
  }[typeInspection] || 'Inspection';

  const couleurType = typeInspection === 'pre_location' ? 'border-blue-500' : typeInspection === 'post_location' ? 'border-purple-500' : 'border-emerald-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="inspection-dialog">
      <div className={`bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto border-t-4 ${couleurType}`} onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900">{titrePartType}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {equipementNom}
              {clientNom && <span> · client : <strong>{clientNom}</strong></span>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-3">
          <div className="space-y-1.5">
            {items.map(it => (
              <div key={it.id} className="flex items-start gap-2 p-1.5 rounded hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={it.checked}
                  onChange={() => toggleItem(it.id)}
                  className="mt-1 w-4 h-4 rounded border-slate-300 text-emerald-600"
                  data-testid={`inspection-item-${it.id}`}
                />
                <div className="flex-1 min-w-0">
                  <label
                    className={`text-xs font-medium cursor-pointer ${it.checked ? 'text-slate-700' : 'text-red-700'}`}
                    onClick={() => toggleItem(it.id)}
                  >
                    {it.label}{' '}
                    {!it.checked && <span className="text-[10px] bg-red-100 text-red-700 px-1 rounded">NON CONFORME</span>}
                  </label>
                  {!it.checked && (
                    <Input
                      value={it.notes || ''}
                      onChange={e => updateItemNotes(it.id, e.target.value)}
                      placeholder="Décrire le problème (rouille, fuite, dommage...)"
                      className="mt-1 h-7 text-xs"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          <Textarea
            value={notesGen}
            onChange={e => setNotesGen(e.target.value)}
            placeholder="Notes générales (optionnel) — état du carburant, kilométrage, etc."
            rows={2}
            className="text-xs"
          />

          <div className="border rounded p-2 bg-slate-50">
            <p className="text-[11px] font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
              <Camera className="w-3 h-3" /> Photos ({photos.length})
            </p>
            <div className="flex flex-wrap gap-2">
              <label className="cursor-pointer inline-flex items-center gap-1 text-xs bg-white hover:bg-slate-100 px-2 py-1 rounded border">
                <Camera className="w-3 h-3" /> {uploadingPhoto ? '…' : '+ Photo'}
                <input
                  type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value=''; }}
                />
              </label>
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p.startsWith('http') ? p : `${API_URL}${p}`} alt="" className="w-14 h-14 object-cover rounded border" />
                  <button
                    onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs">
            {items.every(i => i.checked) ? (
              <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 font-semibold flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Conforme
              </span>
            ) : (
              <span className="text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 font-semibold flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Non conforme — {items.filter(i => !i.checked).length} problème{items.filter(i => !i.checked).length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="btn-save-inspection-dialog"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer l\'inspection'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default InspectionDialog;
